import { Link } from "wouter";
import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { ArrowRight, CalendarDays, Bell, BarChart3, Users, Pencil, MessageSquare, Calendar, CreditCard, Clock, ChevronDown } from "lucide-react";

// ─── Rolling number hook ───────────────────────────────────────────────────
function useRollingNumber(target: number, duration = 700) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number | undefined>(undefined);
  const startRef = useRef<{ t: number; from: number } | null>(null);
  useEffect(() => {
    const from = display;
    if (from === target) return;
    startRef.current = { t: performance.now(), from };
    const tick = (now: number) => {
      const s = startRef.current!;
      const p = Math.min((now - s.t) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(s.from + (target - s.from) * e));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);
  return display;
}
import { useSiteConfig } from "@/lib/SiteConfigContext";
import { useStudioConfig } from "@/hooks/use-studio-config";
import { magikVars, magikVarsSmall } from "@/lib/magik-styles";

const STATS = [
  { value: "47+", label: "Studios onboarded" },
  { value: "68%", label: "Avg no-show reduction" },
  { value: "11 hrs", label: "Saved per studio / week" },
  { value: "$1,400+", label: "Avg monthly revenue recovered" },
];

const TESTIMONIALS = [
  {
    quote: "We went from chasing deposits manually to having the system do it for us. First month we recovered $3,200 we would have lost.",
    name: "Marcus T.",
    studio: "Blackline Studio · 4 artists",
    result: "+$3,200 / mo",
  },
  {
    quote: "My artists actually fill their own books now. I check the dashboard once a day and that's it. I stopped being the middleman.",
    name: "Jade R.",
    studio: "Sacred Ink · 3 artists",
    result: "−80% admin time",
  },
  {
    quote: "The no-show rate dropped from 22% to under 5% in six weeks. That alone paid for the platform ten times over.",
    name: "Devon M.",
    studio: "Iron & Ember · 6 artists",
    result: "22% → 5% no-shows",
  },
];

const FAQ_ITEMS = [
  {
    q: "Do my artists need to download anything?",
    a: "No. The artist portal is fully browser-based. They log in with a link you send them — nothing to install, no app store required.",
  },
  {
    q: "How long does setup take?",
    a: "Most studios are live within 48 hours. We handle the initial configuration on a dedicated 60-minute onboarding call — you don't touch a settings menu until everything is already working.",
  },
  {
    q: "Does it work if we still take bookings through Instagram DMs?",
    a: "Yes. Clients still DM you to start — Tattoo Bench takes over after first contact. Deposit collection, reminders, release forms, and confirmations all happen automatically from there.",
  },
  {
    q: "What happens to bookings and history if an artist leaves?",
    a: "Everything stays on your studio's account. Client history, booking records, and payment logs belong to the studio, not the individual artist.",
  },
  {
    q: "Is there a long-term contract?",
    a: "No. Monthly billing, cancel any time. We keep studios because the platform works, not because they're locked in.",
  },
];

const FEATURE_CARDS = [
  {
    icon: Bell,
    color: "text-violet-400",
    bg: "bg-violet-500/10 border-violet-500/20",
    title: "Automated Reminders",
    desc: "Deposit-gated bookings with SMS and email reminders fire automatically. No-shows drop. Revenue recovers itself.",
    // Unsplash: dark moody phone/notification
    image: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=600&q=80&fit=crop&crop=entropy",
  },
  {
    icon: CalendarDays,
    color: "text-sky-400",
    bg: "bg-sky-500/10 border-sky-500/20",
    title: "Booking Management",
    desc: "Artists manage their own schedules through a dedicated portal. One studio calendar. No more scheduling chaos.",
    // Unsplash: planner / calendar
    image: "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=600&q=80&fit=crop&crop=entropy",
  },
  {
    icon: BarChart3,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10 border-emerald-500/20",
    title: "Analytics & Reporting",
    desc: "Daily digest emails surface bookings, revenue, and artist performance. Know what's working before breakfast.",
    // Unsplash: data / analytics screens
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80&fit=crop&crop=entropy",
  },
  {
    icon: Users,
    color: "text-pink-400",
    bg: "bg-pink-500/10 border-pink-500/20",
    title: "Client Portal",
    desc: "Clients review appointments, upload healing photos, and access aftercare from a branded portal — no app download required.",
    // Unsplash: person on device / client interaction
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&q=80&fit=crop&crop=entropy",
  },
];


// ─── Bench hours flow bar segment ─────────────────────────────────────────
function FlowSegment({ pct, color, label, delay = 0 }: { pct: number; color: string; label: string; delay?: number }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(pct), 60 + delay);
    return () => clearTimeout(t);
  }, [pct, delay]);
  if (pct <= 0) return null;
  return (
    <div
      title={label}
      style={{
        width: `${width}%`,
        background: color,
        transition: "width 0.7s cubic-bezier(0.16,1,0.3,1)",
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {pct >= 12 && (
        <span style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: 10, fontWeight: 700, color: "rgba(0,0,0,0.7)",
          letterSpacing: "0.08em", whiteSpace: "nowrap",
        }}>
          {label}
        </span>
      )}
    </div>
  );
}

// ─── Slider control ───────────────────────────────────────────────────────────
function BenchSlider({
  label, value, display, min, max, step, onChange,
}: {
  label: string; value: number; display: string;
  min: number; max: number; step: number;
  onChange: (v: number) => void;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/60">{label}</span>
        <span className="text-sm font-heading font-bold tabular-nums" style={{ color: "rgb(167,139,250)" }}>
          {display}
        </span>
      </div>
      <div className="relative h-7 flex items-center">
        {/* Track bg */}
        <div className="absolute inset-x-0 h-1 rounded-full" style={{ background: "rgba(255,255,255,0.08)" }} />
        {/* Filled track */}
        <div
          className="absolute left-0 h-1 rounded-full"
          style={{ width: `${pct}%`, background: "rgba(139,92,246,0.7)", transition: "width 0.1s" }}
        />
        <input
          type="range"
          min={min} max={max} step={step} value={value}
          onChange={e => onChange(Number(e.target.value))}
          className="absolute inset-x-0 w-full opacity-0 cursor-pointer h-7"
          style={{ zIndex: 2 }}
        />
        {/* Thumb */}
        <div
          className="absolute w-4 h-4 rounded-full border-2 pointer-events-none"
          style={{
            left: `calc(${pct}% - 8px)`,
            background: "rgb(139,92,246)",
            borderColor: "rgba(255,255,255,0.8)",
            boxShadow: "0 0 8px rgba(139,92,246,0.6)",
            transition: "left 0.1s",
            zIndex: 1,
          }}
        />
      </div>
    </div>
  );
}

function BenchTimeSection() {
  const [mode, setMode]               = useState<"before" | "after">("before");
  const [hourlyRate, setHourlyRate]   = useState(100);   // $50–$300 step $5
  const [hoursPerWeek, setHoursPerWeek] = useState(36);  // 10–60 step 5
  const isAfter = mode === "after";

  // Admin hours scale proportionally with total hours (fixed 44.4% ratio, same as baseline 16/36)
  const adminTotal = Math.round(hoursPerWeek * 16 / 36);
  const benchBefore = hoursPerWeek - adminTotal;
  const benchAfter  = hoursPerWeek;

  // Distribute admin across 4 tasks, last task absorbs rounding remainder
  const dmHrs         = Math.round(adminTotal * 6 / 16);
  const schedHrs      = Math.round(adminTotal * 4 / 16);
  const depositHrs    = Math.round(adminTotal * 3 / 16);
  const reminderHrs   = adminTotal - dmHrs - schedHrs - depositHrs;

  // Revenue
  const earnedBefore       = benchBefore * hourlyRate;
  const earnedAfter        = benchAfter  * hourlyRate;
  const weeklyDelta        = earnedAfter - earnedBefore;
  const monthlyEarnedVal   = Math.round((isAfter ? earnedAfter : earnedBefore) * 52 / 12);
  const annualEarnedVal    = (isAfter ? earnedAfter : earnedBefore) * 52;
  const monthlyDeltaVal    = Math.round(weeklyDelta * 52 / 12);
  const annualDeltaVal     = weeklyDelta * 52;
  const benchUtilVal       = isAfter ? 100 : Math.round((benchBefore / hoursPerWeek) * 100);
  const pctUplift          = earnedBefore > 0 ? Math.round((weeklyDelta / earnedBefore) * 100) : 0;

  // Rolling numbers
  const weeklyEarned  = useRollingNumber(isAfter ? earnedAfter  : earnedBefore);
  const monthlyEarned = useRollingNumber(monthlyEarnedVal);
  const annualEarned  = useRollingNumber(annualEarnedVal);
  const benchUtil     = useRollingNumber(benchUtilVal);
  const benchHrsDisp  = useRollingNumber(isAfter ? benchAfter  : benchBefore);
  const adminHrsDisp  = useRollingNumber(isAfter ? 0 : adminTotal);
  const weeklyDeltaDisp   = useRollingNumber(weeklyDelta);
  const monthlyDeltaDisp  = useRollingNumber(monthlyDeltaVal);
  const annualDeltaDisp   = useRollingNumber(annualDeltaVal);

  // Flow bar
  const benchPct = Math.round((isAfter ? benchAfter : benchBefore) / hoursPerWeek * 100);
  const adminPct = 100 - benchPct;

  // Dynamic rows
  const rows = isAfter
    ? [
        { icon: Pencil,        label: "Tattooing (at bench)", hours: hoursPerWeek, earned: earnedAfter, bench: true,  note: ""          },
        { icon: MessageSquare, label: "DMing clients",         hours: 0,            earned: 0,           bench: false, note: "automated" },
        { icon: Calendar,      label: "Scheduling bookings",  hours: 0,            earned: 0,           bench: false, note: "automated" },
        { icon: CreditCard,    label: "Chasing deposits",     hours: 0,            earned: 0,           bench: false, note: "automated" },
        { icon: Clock,         label: "Sending reminders",    hours: 0,            earned: 0,           bench: false, note: "automated" },
      ]
    : [
        { icon: Pencil,        label: "Tattooing (at bench)", hours: benchBefore,  earned: earnedBefore, bench: true,  note: "" },
        { icon: MessageSquare, label: "DMing clients",         hours: dmHrs,        earned: 0,            bench: false, note: "" },
        { icon: Calendar,      label: "Scheduling bookings",  hours: schedHrs,     earned: 0,            bench: false, note: "" },
        { icon: CreditCard,    label: "Chasing deposits",     hours: depositHrs,   earned: 0,            bench: false, note: "" },
        { icon: Clock,         label: "Sending reminders",    hours: reminderHrs,  earned: 0,            bench: false, note: "" },
      ];

  return (
    <section id="bench-label" className="container mx-auto px-6" data-testid="agency-bench-label">
      <div className="max-w-2xl mx-auto">

        {/* Toggle */}
        <div className="flex justify-center mb-3">
          <div className="flex rounded-xl bg-black/80 backdrop-blur-sm border border-white/10 overflow-hidden p-1 gap-1">
            {(["before", "after"] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-4 py-2 rounded-lg text-xs uppercase tracking-[0.15em] font-subheading font-bold transition-all duration-200 ${
                  mode === m
                    ? "bg-accent text-white shadow-[0_0_12px_rgba(139,92,246,0.5)]"
                    : "text-muted-foreground hover:text-white"
                }`}
              >
                {m === "before" ? "Without Tattoo Bench" : "With Tattoo Bench"}
              </button>
            ))}
          </div>
        </div>

        {/* ── Sliders ── */}
        <div
          className="rounded-2xl px-5 py-4 mb-3 flex flex-col gap-3 font-subheading"
          style={{
            background: "rgba(8,6,14,0.95)",
            border: "1px solid rgba(255,255,255,0.07)",
          }}
        >
          <BenchSlider
            label="Your hourly rate"
            value={hourlyRate}
            display={`$${hourlyRate} / hr`}
            min={50} max={300} step={5}
            onChange={setHourlyRate}
          />
          <BenchSlider
            label="Hours worked / week"
            value={hoursPerWeek}
            display={`${hoursPerWeek} hrs`}
            min={10} max={60} step={5}
            onChange={setHoursPerWeek}
          />
        </div>

        {/* Card */}
        <div
          className="rounded-2xl overflow-hidden font-subheading"
          style={{
            background: "rgba(8,6,14,0.98)",
            border: isAfter ? "1px solid rgba(139,92,246,0.4)" : "1px solid rgba(255,255,255,0.08)",
            boxShadow: isAfter
              ? "0 0 48px rgba(139,92,246,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
              : "0 0 20px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.03)",
            transition: "box-shadow 0.5s ease, border-color 0.5s ease",
          }}
        >
          {/* ── KPI header strip ── */}
          <div
            className="grid grid-cols-3 divide-x divide-white/8"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
          >
            {[
              { label: "Bench hrs / wk",    value: `${benchHrsDisp}h`,                              hi: isAfter, testId: "bench-kpi-bench-hrs"  },
              { label: "Admin hrs / wk",    value: adminHrsDisp === 0 ? "0h ✓" : `${adminHrsDisp}h`, hi: false, bad: !isAfter, testId: "bench-kpi-admin-hrs"  },
              { label: "Bench utilisation", value: `${benchUtil}%`,                                  hi: isAfter, testId: "bench-kpi-utilisation" },
            ].map(({ label, value, hi, bad, testId }) => (
              <div key={label} className="flex flex-col items-center py-3 px-3 gap-0.5">
                <span
                  data-testid={testId}
                  className="text-xl font-heading font-bold tabular-nums transition-colors duration-500"
                  style={{ color: hi ? "rgb(167,139,250)" : bad ? "rgb(248,113,113)" : "rgba(255,255,255,0.55)" }}
                >
                  {value}
                </span>
                <span className="text-[9px] uppercase tracking-[0.16em] text-white/55 text-center leading-tight">
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* ── Hours flow bar ── */}
          <div className="px-5 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/55">
                {hoursPerWeek} hrs / week — how time is allocated
              </span>
              {isAfter && (
                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.15em]">
                  +{adminTotal}h reclaimed →
                </span>
              )}
            </div>
            <div
              className="flex rounded-lg overflow-hidden"
              style={{ height: 28, background: "rgba(255,255,255,0.04)" }}
            >
              <FlowSegment pct={benchPct} color="rgba(139,92,246,0.85)" label={`${isAfter ? benchAfter : benchBefore}h bench`} delay={0} />
              <FlowSegment pct={adminPct} color="rgba(239,68,68,0.5)"   label={`${adminTotal}h admin`} delay={80} />
            </div>
            <div className="flex gap-4 mt-2">
              <span className="flex items-center gap-1.5 text-[10px] text-white/60">
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "rgba(139,92,246,0.85)" }} />
                Tattooing (billable)
              </span>
              {!isAfter && (
                <span className="flex items-center gap-1.5 text-[10px] text-white/60">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "rgba(239,68,68,0.5)" }} />
                  Admin (unbillable)
                </span>
              )}
              {isAfter && (
                <span className="flex items-center gap-1.5 text-[10px] text-emerald-400/70">
                  All {hoursPerWeek} hrs billable — admin automated
                </span>
              )}
            </div>
          </div>

          {/* ── Activity rows ── */}
          <div className="divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
            {rows.map((row) => {
              const Icon = row.icon;
              return (
                <div
                  key={row.label}
                  className="flex items-center px-5 py-2 transition-colors"
                  style={{ background: row.bench ? "rgba(139,92,246,0.04)" : "transparent" }}
                >
                  <Icon
                    size={13}
                    className="mr-3 shrink-0"
                    style={{ color: row.bench ? "rgb(167,139,250)" : "rgba(255,255,255,0.2)" }}
                  />
                  <span
                    className="flex-1 text-sm"
                    style={{ color: row.bench ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.45)", fontWeight: row.bench ? 600 : 400 }}
                  >
                    {row.label}
                    {row.note && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider font-bold" style={{ color: "rgb(52,211,153)" }}>
                        {row.note}
                      </span>
                    )}
                  </span>
                  <span
                    className="w-14 text-right text-sm tabular-nums"
                    style={{
                      color: row.bench ? "rgba(255,255,255,0.8)" : row.hours === 0 ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.4)",
                      textDecoration: row.hours === 0 && !row.bench ? "line-through" : "none",
                      fontWeight: row.bench ? 600 : 400,
                    }}
                  >
                    {row.hours === 0 ? "0h" : `${row.hours}h`}
                  </span>
                  <span
                    className="w-24 text-right text-sm font-bold tabular-nums"
                    style={{ color: row.earned > 0 ? "rgb(167,139,250)" : "rgba(255,255,255,0.18)" }}
                  >
                    {row.earned > 0 ? `$${row.earned.toLocaleString()}` : "—"}
                  </span>
                </div>
              );
            })}
          </div>

          {/* ── Revenue summary ── */}
          <div style={{ borderTop: "2px solid rgba(255,255,255,0.12)", background: "rgba(139,92,246,0.05)" }}>
            <div className="grid grid-cols-3 divide-x divide-white/6 px-0">
              {[
                { period: "/ week",  primary: weeklyEarned,  delta: weeklyDeltaDisp,  testId: "bench-weekly-earned"  },
                { period: "/ month", primary: monthlyEarned, delta: monthlyDeltaDisp, testId: "bench-monthly-earned" },
                { period: "/ year",  primary: annualEarned,  delta: annualDeltaDisp,  testId: "bench-annual-earned"  },
              ].map(({ period, primary, delta, testId }) => (
                <div key={period} className="flex flex-col items-center py-2.5 px-3 gap-0">
                  <span data-testid={testId} className="text-lg font-heading font-bold tabular-nums" style={{ color: "rgb(167,139,250)" }}>
                    ${primary.toLocaleString()}
                  </span>
                  {isAfter && (
                    <span className="text-[10px] font-bold tabular-nums text-emerald-400">
                      +${delta.toLocaleString()}
                    </span>
                  )}
                  <span className="text-[9px] uppercase tracking-[0.16em] text-white/50">{period}</span>
                </div>
              ))}
            </div>

            {/* Delta callout */}
            <div
              className="mx-3 mb-3 rounded-xl px-4 py-3 flex items-center gap-3 transition-all duration-500"
              style={{
                background: isAfter ? "rgba(52,211,153,0.07)" : "rgba(239,68,68,0.07)",
                border: isAfter ? "1px solid rgba(52,211,153,0.25)" : "1px solid rgba(239,68,68,0.2)",
              }}
            >
              <span
                className="text-4xl font-heading font-bold tabular-nums transition-colors duration-500 shrink-0"
                style={{ color: isAfter ? "rgb(52,211,153)" : "rgb(248,113,113)" }}
              >
                {isAfter ? `+${pctUplift}%` : `−${pctUplift}%`}
              </span>
              <div className="flex flex-col flex-1 gap-0.5">
                <span
                  className="text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-500"
                  style={{ color: isAfter ? "rgb(52,211,153)" : "rgb(248,113,113)" }}
                >
                  {isAfter ? "Revenue uplift vs. without" : "Revenue lost to admin work"}
                </span>
                <span
                  className="text-sm font-semibold tabular-nums transition-colors duration-500"
                  style={{ color: isAfter ? "rgba(52,211,153,0.8)" : "rgba(248,113,113,0.7)" }}
                >
                  {isAfter
                    ? `+$${weeklyDelta.toLocaleString()} / wk · ${adminTotal} hrs returned to the bench`
                    : `${adminTotal} hrs / wk earned $0 — could have earned $${weeklyDelta.toLocaleString()}`}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-[11px] text-white/50 mt-4 leading-relaxed">
          {isAfter
            ? "Scheduling, deposits & reminders handled automatically. Every reclaimed hour goes straight back to the bench."
            : "Only bench hours generate revenue. Every admin hour is an opportunity cost — time you can't bill."}
        </p>
      </div>
    </section>
  );
}

export default function AgencyHome() {
  const { config } = useSiteConfig();
  const { config: studio } = useStudioConfig();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const playlist = useMemo(() => {
    const raw = studio.heroVideoPlaylist;
    if (raw && raw.length > 0) return raw;
    const fallbackUrl = studio.heroVideoUrl || "/storage/videos/hero-bg.mp4";
    return [{ url: fallbackUrl, loopStart: studio.heroVideoLoopStart ?? 2.0, loopEnd: null }];
  }, [studio.heroVideoPlaylist, studio.heroVideoUrl, studio.heroVideoLoopStart]);

  // Seek to loopStart on first load so the video doesn't start at the black frame
  useEffect(() => {
    const v = videoRef.current;
    if (!v || playlist.length === 0) return;
    const start = playlist[0].loopStart ?? 0;
    if (start > 0) {
      v.addEventListener("loadedmetadata", () => { v.currentTime = start; }, { once: true });
    }
  }, [playlist]);

  const heroGlowStyle = useMemo(
    () => magikVars(config.magikGlowColor1, config.magikGlowColor2, config.magikGlowIntensity, config.magikGlowSpeed),
    [config.magikGlowColor1, config.magikGlowColor2, config.magikGlowIntensity, config.magikGlowSpeed],
  );
  const smallGlowStyle = useMemo(
    () => magikVarsSmall(config.magikGlowColor1, config.magikGlowColor2, config.magikGlowIntensity, config.magikGlowSpeed),
    [config.magikGlowColor1, config.magikGlowColor2, config.magikGlowIntensity, config.magikGlowSpeed],
  );

  return (
    <div className="space-y-24 pb-24 overflow-x-hidden">
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section
        className="relative min-h-screen flex items-center justify-center -mt-20 pt-20 px-4 md:px-6"
        data-testid="agency-hero"
      >
        <div className="absolute inset-0 z-0" style={{ backgroundColor: "hsl(0 0% 5%)" }} />

        <div
          className="relative z-10 w-full max-w-6xl mx-auto rounded-3xl overflow-hidden border border-white/10"
          style={{
            boxShadow:
              "0 0 120px rgba(139,92,246,0.15), 0 0 60px rgba(236,72,153,0.08), 0 0 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)",
          }}
        >
          {/* Card bg */}
          <div
            className="absolute inset-0 rounded-3xl"
            style={{
              background: "linear-gradient(135deg, rgb(15,12,20) 0%, rgb(10,8,16) 40%, rgb(20,14,30) 100%)",
              opacity: config.heroCardBgOpacity / 100,
            }}
          />

          {/* Hero video */}
          <div className="hero-video-bg hero-video-fade-wrapper absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              loop
              preload="auto"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: (config.heroVideoOpacity ?? 18) / 100,
                filter: "saturate(0.6) brightness(0.7)",
              }}
              data-testid="agency-hero-video"
            >
              <source src={playlist[0]?.url || "/storage/videos/hero-bg.mp4"} type="video/mp4" />
            </video>
          </div>

          {/* Wisps */}
          {(config.heroWispsEnabled ?? true) && (
            <div className="hero-wisps absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
              <div
                className="hero-wisp absolute sm:w-[400px] sm:h-[400px] w-[200px] h-[200px] rounded-full blur-[80px]"
                style={{
                  top: "-15%",
                  right: "-8%",
                  background: `radial-gradient(circle, ${config.magikGlowColor1} 0%, transparent 70%)`,
                  opacity: 0.3,
                  animation: `wispFloat1 ${config.magikGlowSpeed * 3}s ease-in-out infinite alternate`,
                }}
              />
              <div
                className="hero-wisp absolute sm:w-[450px] sm:h-[350px] w-[200px] h-[175px] rounded-full blur-[80px]"
                style={{
                  bottom: "-15%",
                  left: "-12%",
                  background: `radial-gradient(ellipse, ${config.magikGlowColor2} 0%, transparent 70%)`,
                  opacity: 0.25,
                  animation: `wispFloat2 ${config.magikGlowSpeed * 3.5}s ease-in-out infinite alternate-reverse`,
                }}
              />
            </div>
          )}

          <div className="relative z-10 text-center px-6 md:px-12 py-24 md:py-32 lg:py-40">
            {/* Backdrop scrim — tight around text, not the whole card */}
            <div
              className="mx-auto max-w-3xl px-8 py-10 mb-10"
              style={config.heroTextBackdropEnabled ? {
                background: (() => {
                  const h = config.heroTextBackdropColor ?? "#000000";
                  const r = parseInt(h.slice(1, 3), 16);
                  const g = parseInt(h.slice(3, 5), 16);
                  const b = parseInt(h.slice(5, 7), 16);
                  return `rgba(${r},${g},${b},${(config.heroTextBackdropOpacity ?? 45) / 100})`;
                })(),
                backdropFilter: (config.heroTextBackdropBlur ?? 0) > 0
                  ? `blur(${config.heroTextBackdropBlur}px)` : undefined,
                borderRadius: config.heroTextBackdropRadius ?? 24,
              } : undefined}
            >
              <h2 className="text-white uppercase tracking-[0.3em] text-sm md:text-base font-bold mb-4 font-subheading">
                Built for tattoo studio owners
              </h2>
              <h1
                className="text-5xl md:text-6xl lg:text-7xl font-heading mb-6 text-white tracking-wider leading-tight"
                style={
                  config.heroTextShadow > 0
                    ? { textShadow: `0 2px ${config.heroTextShadow}px rgba(0,0,0,0.9), 0 4px ${config.heroTextShadow * 2}px rgba(0,0,0,0.6)` }
                    : undefined
                }
              >
                <span className="magik-glow-text italic" style={heroGlowStyle}>
                  Tattoo Bench
                </span>
              </h1>

              {/* Feature ticker */}
              <div className="relative w-full overflow-hidden mb-8 py-2"
                style={{
                  maskImage: "linear-gradient(to right, transparent 0%, black 80px, black calc(100% - 80px), transparent 100%)",
                  WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 80px, black calc(100% - 80px), transparent 100%)",
                }}>
                <div
                  className="flex whitespace-nowrap"
                  style={{
                    width: "max-content",
                    willChange: "transform",
                    animation: `agency-ticker ${config.heroTickerSpeed ?? 28}s linear infinite`,
                  }}
                >
                  {[
                    "Bookings", "Deposits", "Release Forms", "Email Reminders",
                    "Artist Utilization", "Client Portal",
                    "Walk-in Requests", "Events", "Waitlists", "Intake Forms",
                    "Consultation Requests", "Revenue Analytics", "Scheduling",
                    "Marketing Drips", "Appointment Reminders", "Refund Tracking",
                  ].concat([
                    "Bookings", "Deposits", "Release Forms", "Email Reminders",
                    "Artist Utilization", "Client Portal",
                    "Walk-in Requests", "Events", "Waitlists", "Intake Forms",
                    "Consultation Requests", "Revenue Analytics", "Scheduling",
                    "Marketing Drips", "Appointment Reminders", "Refund Tracking",
                  ]).map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-2">
                      <span className="text-xs uppercase tracking-[0.2em] font-subheading font-bold text-white/90 px-1.5">
                        {f}
                      </span>
                      <span className="text-accent/60 text-lg leading-none select-none">·</span>
                    </span>
                  ))}
                </div>
                <style>{`
                  @keyframes agency-ticker {
                    from { transform: translate3d(0, 0, 0); }
                    to   { transform: translate3d(-50%, 0, 0); }
                  }
                `}</style>
              </div>

              <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-0 font-light tracking-wide leading-relaxed font-subheading">
                One platform that handles your booking calendar, deposit collection, client reminders,
                artist scheduling, and marketing — so you can stop managing tools and start running a business.
              </p>
            </div>

            <div className="flex justify-center">
              <Link
                href="/netglowup/studio-owner/watch?ref=hero"
                className="group bg-accent text-white px-8 py-4 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-all duration-300 flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)]"
                data-testid="agency-hero-cta-primary"
              >
                Watch the Platform Walkthrough{" "}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bench hook ────────────────────────────────────────────────── */}
      <section className="container mx-auto px-6" data-testid="agency-bench-hook">
        <div
          className="rounded-2xl px-8 py-12 md:px-16 md:py-16 text-center relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(8,6,14,0.95) 0%, rgba(15,10,24,0.95) 100%)",
            border: "1px solid rgba(139,92,246,0.2)",
            boxShadow: "0 0 60px rgba(139,92,246,0.08), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          {/* subtle glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at 50% 100%, ${config.magikGlowColor2} 0%, transparent 65%)`,
              opacity: 0.08,
            }}
          />
          <p className="relative text-xs uppercase tracking-[0.35em] font-subheading font-bold text-muted-foreground mb-6">
            The bench principle
          </p>
          <blockquote className="relative text-2xl md:text-3xl lg:text-4xl font-heading text-white leading-snug tracking-wide max-w-4xl mx-auto">
            <p className="mb-8">
              A good body builder knows their gains come from time spent{" "}
              <span className="italic" style={{ color: config.magikGlowColor1 }}>under</span>{" "}
              the bench.
            </p>
            <div
              className="mx-auto mb-8"
              style={{
                height: 1,
                maxWidth: 320,
                background: "linear-gradient(to right, transparent, rgba(139,92,246,0.35), transparent)",
              }}
            />
            <p className="text-gray-400">
              A good tattoo artist knows their gains come from time spent{" "}
              <span className="magik-glow-text italic" style={heroGlowStyle}>at</span>{" "}
              the bench.
            </p>
          </blockquote>
          <p
            className="relative mt-10 text-3xl md:text-4xl font-heading tracking-wide magik-glow-text"
            style={heroGlowStyle}
          >
            How much do you bench?
          </p>
        </div>
      </section>

      {/* ── Time on bench label ───────────────────────────────────────── */}
      <BenchTimeSection />

      {/* ── Features grid ─────────────────────────────────────────────── */}
      <section
        className="relative"
        data-testid="agency-features"
        style={{ background: "linear-gradient(to bottom, rgba(6,4,12,0) 0%, rgba(6,4,12,0.7) 6%, rgba(6,4,12,1) 14%, rgba(6,4,12,1) 96%, rgba(6,4,12,0) 100%)" }}
      >
        <div className="container mx-auto px-6 py-24">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-heading text-white mb-3 tracking-wide">
              <span className="magik-glow-text italic" style={smallGlowStyle}>
                Everything
              </span>{" "}
              in one place
            </h2>
            <p className="text-muted-foreground font-light max-w-xl mx-auto">
              Stop duct-taping five tools together. Tattoo Bench replaces your booking app, email platform,
              client database, and analytics dashboard — configured for tattoo studios out of the box.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURE_CARDS.map(({ icon: Icon, color, bg, title, desc, image }) => (
              <div
                key={title}
                className={`rounded-2xl border hover:brightness-110 transition-all relative overflow-hidden ${bg}`}
                style={{ background: "rgba(8,6,14,0.95)" }}
                data-testid={`agency-feature-${title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {/* Mobile only: Unsplash image as subtle atmospheric texture */}
                <div
                  className="absolute inset-0 sm:hidden pointer-events-none"
                  style={{
                    backgroundImage: `url("${image}")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity: 0.14,
                    filter: "grayscale(20%)",
                  }}
                />
                {/* Bottom vignette so text stays readable over the image */}
                <div
                  className="absolute inset-0 sm:hidden pointer-events-none"
                  style={{ background: "linear-gradient(to bottom, rgba(8,6,14,0.2) 0%, rgba(8,6,14,0.88) 65%)" }}
                />

                {/* Content — centered on mobile, left-aligned on sm+ */}
                <div className="relative p-6 flex flex-col items-center text-center sm:items-start sm:text-left">
                  <div className={`mb-4 w-12 h-12 rounded-xl flex items-center justify-center ${bg}`}>
                    <Icon size={22} className={color} />
                  </div>
                  <h3 className="text-white font-heading text-lg tracking-wide mb-2">{title}</h3>
                  <p className="text-muted-foreground text-sm font-light leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA strip ─────────────────────────────────────────────────── */}
      <section className="container mx-auto px-6">
        <div className="rounded-2xl p-10 md:p-14 bg-black/80 backdrop-blur-sm border border-white/10 text-center relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at 50% 0%, ${config.magikGlowColor1} 0%, transparent 70%)`,
            }}
          />
          <p className="relative text-xs uppercase tracking-[0.3em] font-subheading font-bold text-muted-foreground mb-3">
            For multi-artist studio owners
          </p>
          <h2 className="relative text-3xl md:text-4xl font-heading text-white mb-4 tracking-wide">
            Stop eating no-shows and flying blind
          </h2>
          <p className="relative text-muted-foreground font-light max-w-lg mx-auto mb-8">
            You're not just managing bookings. You're managing artists who may or may not follow up,
            collect deposits, or send release forms. Watch this first.
          </p>
          <div className="relative flex justify-center">
            <Link
              href="/netglowup/studio-owner/watch?ref=cta-strip"
              className="group bg-accent text-white px-10 py-4 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-all duration-300 flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(139,92,246,0.5)] hover:shadow-[0_0_32px_rgba(139,92,246,0.7)]"
              data-testid="agency-cta-strip-watch"
            >
              Watch the Platform Walkthrough <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats strip — hidden until KPIs are verified ───────────────── */}
      {/* <section className="container mx-auto px-6" data-testid="agency-stats">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/10">
          {STATS.map(({ value, label }) => (
            <div key={label} className="bg-zinc-950 px-6 py-8 text-center">
              <div className="text-3xl md:text-4xl font-heading text-white mb-1">{value}</div>
              <div className="text-xs uppercase tracking-widest font-subheading text-zinc-500">{label}</div>
            </div>
          ))}
        </div>
      </section> */}

      {/* ── Testimonials ───────────────────────────────────────────────── */}
      <section className="container mx-auto px-6" data-testid="agency-testimonials">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.3em] font-subheading font-bold text-muted-foreground mb-3">
            From Studio Owners
          </p>
          <h2 className="text-3xl md:text-4xl font-heading text-white tracking-wide">
            Real studios. Real results.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map(({ quote, name, studio, result }) => (
            <div key={name} className="rounded-2xl p-6 bg-zinc-900/80 border border-white/10 flex flex-col gap-4">
              <p className="text-zinc-400 font-light text-sm leading-relaxed flex-1">"{quote}"</p>
              <div className="border-t border-white/10 pt-4 flex items-start justify-between gap-3">
                <div>
                  <div className="text-white font-subheading font-bold text-sm">{name}</div>
                  <div className="text-zinc-500 text-xs mt-0.5">{studio}</div>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold font-subheading px-3 py-1.5 rounded-full shrink-0 whitespace-nowrap">
                  {result}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      <section className="container mx-auto px-6 pb-10" data-testid="agency-faq">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.3em] font-subheading font-bold text-muted-foreground mb-3">
            Common Questions
          </p>
          <h2 className="text-3xl md:text-4xl font-heading text-white tracking-wide">
            Before you watch
          </h2>
        </div>
        <div className="max-w-2xl mx-auto flex flex-col divide-y divide-white/10 border border-white/10 rounded-2xl overflow-hidden">
          {FAQ_ITEMS.map(({ q, a }, i) => (
            <div key={i} className="bg-zinc-900/80">
              <button
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                data-testid={`agency-faq-${i}`}
              >
                <span className="text-white font-subheading font-bold text-sm">{q}</span>
                <ChevronDown
                  size={16}
                  className={`text-zinc-500 shrink-0 transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`}
                />
              </button>
              {openFaq === i && (
                <div className="px-6 pb-5">
                  <p className="text-zinc-400 text-sm font-light leading-relaxed">{a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────────────────────────── */}
      <section className="container mx-auto px-6 pt-0 pb-12 text-center" data-testid="agency-final-cta">
        <img
          src="/brand/cinematic-dark.png"
          alt="Tattoo Bench"
          className="mx-auto mb-10 w-full max-w-xl object-contain"
        />
        <a
          href={`/netglowup/studio-owner/watch?ref=footer-cta`}
          className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl font-subheading font-bold text-lg text-white transition-all duration-200 hover:scale-105 hover:brightness-110"
          style={{
            background: "linear-gradient(135deg, rgba(139,92,246,0.9) 0%, rgba(109,40,217,0.9) 100%)",
            boxShadow: "0 0 40px rgba(139,92,246,0.35), inset 0 1px 0 rgba(255,255,255,0.12)",
          }}
        >
          Watch the Platform Walkthrough
        </a>
      </section>
    </div>
  );
}
